import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
// Component-specific imports
import { QueueAddUpdateModal, Filter, Header } from "./component";
import { DataTable, DeleteModal, Excel } from "CommonComponent";

import { fetchQueues, getQueueById, deleteQueue, exportQueues } from "actions";

import AccessRights from "utils/AccessRights";

const Queues = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // --- Redux State ---
  const queueState = useSelector((s) => s.queueState);
  const session = useSelector((s) => s.session);

  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  // --- Local State ---
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [extraFiltersState, setExtraFiltersState] = useState({ values: {} });

  // --- Fetch queues ---
  useEffect(() => {
    if (
      session.current_page_permissions &&
      session.current_page_permissions.page_url === location.pathname
    )
      dispatch(fetchQueues({ ...extraFiltersState.values, object_viewed_id }));
  }, [session.current_page_permissions]);

  // Reload after CUD operations
  useEffect(() => {
    if (queueState.redirectToList) {
      dispatch(fetchQueues({ ...extraFiltersState.values, object_viewed_id }));
    }
  }, [queueState.redirectToList, session.current_page_permissions]);

  // Close modal after update
  useEffect(() => {
    if (!queueState.showUpdateForm) {
      setIsEdit(false);
      setShowAddUpdateModal(false);
    }
  }, [queueState.showUpdateForm]);

  // --- CRUD Handlers ---
  const handleAdd = () => {
    setIsEdit(false);
    setSelectedRow(null);
    setShowAddUpdateModal(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsEdit(true);
    setShowAddUpdateModal(true);
    dispatch(getQueueById(row.id, "update", object_viewed_id));
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!selectedRow) return;
    dispatch(deleteQueue(selectedRow.id, object_viewed_id));
    setShowDeleteModal(false);
  };

  // --- Action Column ---
 const getActions = (row) => (
    <div
      className="actionClass"
      style={{ textAlign: "center", whiteSpace: "nowrap" }}
    >
      {AccessRights(session.current_page_permissions, "edit", row.created_by) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-edit-${row.id}`}>Edit</Tooltip>}
          >
            <img
              src="/assets/images/edit-icon.svg"
              alt="Edit"
              onClick={() => handleEdit(row)}
              style={{ cursor: "pointer", marginRight: "10px" }}
            />
          </OverlayTrigger>
        )}

      {AccessRights(
        session.current_page_permissions,
        "delete",
        row.created_by,
      ) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-delete-${row.id}`}>Delete</Tooltip>}
          >
            <img
              src="/assets/images/delete-icon.svg"
              alt="Delete"
              onClick={() => handleDelete(row)}
              style={{ cursor: "pointer" }}
            />
          </OverlayTrigger>
        )}
    </div>
  );


  // --- Table Columns ---
  const columns = useMemo(
    () => [
      {
        id: "queue_name",
        header: "Queue Name",
        accessor: (row) => row.queue_name,
        sortable: true,
      },
      {
        id: "brand",
        header: "Brand",
        accessor: (row) => row.brand?.brand_name || "-",
        sortable: true,
      },
      {
        id: "direction",
        header: "Direction",
        accessor: (row) => row.direction?.value || "-",
        sortable: true,
      },
      {
        id: "language",
        header: "Language",
        accessor: (row) => row.language?.value || "-",
        sortable: true,
      },
      {
        id: "business",
        header: "Business",
        accessor: (row) => row.business?.value || "-",
        sortable: true,
      },
      {
        id: "geography",
        header: "Geography",
        accessor: (row) => row.geography?.value || "-",
        sortable: true,
      },
      {
        id: "source",
        header: "Source",
        accessor: (row) => row.source?.value || "-",
        sortable: true,
      },
      {
        id: "actions",
        header: "Actions",
        accessor: null,
        cell: (_, row) => getActions(row),
        sortable: false,
      },
    ],
    [session.current_page_permissions]
  );

  const rows = queueState.queuesList || [];

  // --- Filter & Export ---
  const filterTable = () => {
    dispatch(fetchQueues({ ...extraFiltersState.values, object_viewed_id }));
  };

  const handleExport = () => {
    dispatch(exportQueues(extraFiltersState.values, object_viewed_id));
  };

  // --- Render ---
  return (
    <div className="wrapper-div">
      <Header onAdd={handleAdd} object_viewed_id={object_viewed_id} />

      <div className="d-flex justify-content-end align-items-center pb-4 px-4 position-sticky top-70 bg-white z-1">
        <div className="d-flex align-items-center gap-3">
          <Excel handleExport={handleExport} />
        </div>
      </div>
      <Filter
        onFilter={filterTable}
        extraFiltersState={extraFiltersState}
        setExtraFiltersState={setExtraFiltersState}
      />
      <DataTable
        columns={columns}
        data={rows}
        serverSide={false}
        showSearch={false}
        className="table-container"
      />

      {showAddUpdateModal && (
        <QueueAddUpdateModal
          show={showAddUpdateModal}
          handleClose={() => setShowAddUpdateModal(false)}
          isEdit={isEdit}
          data={selectedRow}
          object_viewed_id={object_viewed_id}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={confirmDelete}
          message="Are you sure you want to delete this queue?"
        />
      )}
    </div>
  );
};

export default Queues;
